import { useState, useCallback, useEffect } from 'react';
import { Comment, CommentInput } from '../../../models/Comment';
import { CommentsService } from '../../services/comments/comments-service';
import { auth } from '../../../config/firebase-config';

// Updated to support guest comments
interface UseCommentsResult {
  comments: Comment[];
  loading: boolean;
  error: string | null;
  lastUpdate: Date | null;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
  totalCount: number;
  isFromCache: boolean;
  addComment: (commentInput: CommentInput, userId: string, userName: string, userProfilePicture?: string, userEmail?: string, userPhone?: string) => Promise<void>;
  refreshComments: () => Promise<void>;
  nextPage: () => Promise<void>;
  prevPage: () => Promise<void>;
  goToPage: (page: number) => Promise<void>;
}

export const useComments = (profileId: string): UseCommentsResult => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isFromCache, setIsFromCache] = useState(false);

  // Cache for loaded pages to prevent re-fetching
  const [pageCache, setPageCache] = useState<Map<number, {comments: Comment[], totalCount: number, hasMore: boolean, timestamp: Date}>>(new Map());

  const COMMENTS_PER_PAGE = 5;
  const totalPages = Math.ceil(totalCount / COMMENTS_PER_PAGE);

  const fetchComments = useCallback(async (page: number = currentPage) => {
    if (!profileId) return;

    // Check cache first
    const cachedPage = pageCache.get(page);
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

    if (cachedPage && (Date.now() - cachedPage.timestamp.getTime()) < CACHE_DURATION) {
      // Use cached data
      setComments(cachedPage.comments);
      setTotalCount(cachedPage.totalCount);
      setHasMore(cachedPage.hasMore);
      setCurrentPage(page);
      setLastUpdate(cachedPage.timestamp);
      setIsFromCache(true);
      return;
    }

    // Fetch from Firestore
    setLoading(true);
    setError(null);
    try {
      const result = await CommentsService.getCommentsForProfile(profileId, page, COMMENTS_PER_PAGE);
      
      // Update cache
      setPageCache(prev => new Map(prev).set(page, {
        comments: result.comments,
        totalCount: result.totalCount,
        hasMore: result.hasMore,
        timestamp: new Date()
      }));
      
      setComments(result.comments);
      setTotalCount(result.totalCount);
      setHasMore(result.hasMore);
      setCurrentPage(page);
      setLastUpdate(new Date());
      setIsFromCache(false);
    } catch (err) {
      setError('Failed to load comments');
      console.error('Error fetching comments:', err);
    } finally {
      setLoading(false);
    }
  }, [profileId, currentPage, pageCache]);

  const addComment = useCallback(async (
    commentInput: CommentInput,
    userId: string,
    userName: string,
    userProfilePicture?: string,
    userEmail?: string,
    userPhone?: string
  ) => {
    console.log('🔄 Starting comment submission...', { commentInput, userId, userName });

    try {
      // For guests, userId will start with 'guest-' and no authentication is needed
      // For authenticated users, check they're logged in
      if (!userId.startsWith('guest-')) {
        const user = auth.currentUser;
        console.log('👤 Auth check:', { user: user ? 'authenticated' : 'not authenticated', uid: user?.uid });

        if (!user) {
          console.log('❌ Comment blocked: not authenticated');
          throw new Error('You must be logged in to comment');
        }
      } else {
        console.log('👤 Guest comment submission');
      }

      console.log('💾 Saving comment to Firestore...');
      const newComment = await CommentsService.addComment(commentInput, userId, userName, userProfilePicture, userEmail, userPhone);
      console.log('✅ Comment saved:', newComment.commentId);

      console.log('📱 Updating local state...');
      setComments(prev => [newComment, ...prev]);

      // Clear cache since new comment affects all pages
      setPageCache(new Map());

      setLastUpdate(new Date());

      console.log('🎉 Comment submission complete!');
    } catch (err) {
      console.error('❌ Error adding comment:', err);
      throw err;
    }
  }, []);

  // Manual refresh only - reloads current page and clears cache
  const refreshComments = useCallback(async () => {
    // Clear cache for current page to force fresh fetch
    setPageCache(prev => {
      const newCache = new Map(prev);
      newCache.delete(currentPage);
      return newCache;
    });
    await fetchComments(currentPage);
  }, [fetchComments, currentPage]);

  // Pagination functions
  const nextPage = useCallback(async () => {
    if (hasMore) {
      await fetchComments(currentPage + 1);
    }
  }, [fetchComments, currentPage, hasMore]);

  const prevPage = useCallback(async () => {
    if (currentPage > 1) {
      await fetchComments(currentPage - 1);
    }
  }, [fetchComments, currentPage]);

  const goToPage = useCallback(async (page: number) => {
    if (page >= 1 && page <= totalPages) {
      await fetchComments(page);
    }
  }, [fetchComments, totalPages]);

  // Load comments initially
  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  return {
    comments,
    loading,
    error,
    lastUpdate,
    currentPage,
    totalPages,
    hasMore,
    totalCount,
    isFromCache,
    addComment,
    refreshComments,
    nextPage,
    prevPage,
    goToPage,
  };
};
